import {UserServiceClient} from '@multipass-user-service';
import {UserReasonCodes} from '@multipass-user-service-dto';
import {Logger} from 'esp-js';

import {ReasonError} from '../reasonError';
import {Session, SessionMapper} from './sessionMapper';

const _log = Logger.create('SessionService');

export type SendLoginCodeOutcome =
    | {readonly kind: 'codeSent'}
    | {readonly kind: 'accountNotFound'; readonly code: string; readonly message: string};

export type VerifyOutcome =
    | {readonly kind: 'session'; readonly session: Session}
    | {readonly kind: 'awaiting-terms'; readonly outstandingLegalDocumentIds: string[]; readonly signInTicket: string};

export interface SessionService {
    sendLoginCode(email: string, locale: string): Promise<SendLoginCodeOutcome>;
    verifyLoginCode(email: string, code: string): Promise<VerifyOutcome>;
    acceptTermsAndSignIn(signInTicket: string, acceptedDocumentIds: string[]): Promise<Session>;
    restoreSession(): Promise<Session | null>;
    signOut(): Promise<void>;
}

export class DefaultSessionService implements SessionService {
    #sessionMapper = new SessionMapper();
    readonly #userServiceClient: UserServiceClient;

    constructor(userServiceClient: UserServiceClient) {
        this.#userServiceClient = userServiceClient;
    }

    public async sendLoginCode(email: string, locale: string): Promise<SendLoginCodeOutcome> {
        const response = await this.#userServiceClient.requestSendLoginCode({email, locale});
        const reason = response.errors?.[0];
        if (reason == null) {
            return {kind: 'codeSent'};
        }
        if (reason.code === UserReasonCodes.AccountNotFound) {
            return {kind: 'accountNotFound', code: reason.code, message: reason.message};
        }
        throw new ReasonError(reason.code, reason.message);
    }

    public async verifyLoginCode(email: string, code: string): Promise<VerifyOutcome> {
        const response = await this.#userServiceClient.requestVerifyLoginCode({email, code});
        const errors = response.errors;
        if (errors != null && errors.length > 0) {
            throw new ReasonError(errors[0].code, errors[0].message);
        }
        const outstanding = response.outstandingLegalDocumentIds;
        if (outstanding != null && outstanding.length > 0 && response.signInTicket != null) {
            return {
                kind: 'awaiting-terms',
                outstandingLegalDocumentIds: outstanding.map(id => String(id)),
                signInTicket: response.signInTicket,
            };
        }
        return {kind: 'session', session: this.#sessionMapper.mapSessionFromDto(response)};
    }

    public async acceptTermsAndSignIn(signInTicket: string, acceptedDocumentIds: string[]): Promise<Session> {
        const response = await this.#userServiceClient.requestAcceptTermsAndSignIn({signInTicket, acceptedDocumentIds});
        const errors = response.errors;
        if (errors != null && errors.length > 0) {
            throw new ReasonError(errors[0].code, errors[0].message);
        }
        return this.#sessionMapper.mapSessionFromAcceptDto(response);
    }

    public async restoreSession(): Promise<Session | null> {
        try {
            const response = await this.#userServiceClient.requestGetUser({userId: ''});
            return this.#sessionMapper.mapSessionFromUserDto(response);
        } catch (error) {
            // No / expired cookie is the common case here — a debug line, not an error.
            _log.debug('Session restore failed; continuing signed out', error);
            return null;
        }
    }

    public async signOut(): Promise<void> {
        // Clears the httpOnly cookie server-side (JS can't); harmless if already signed out.
        await this.#userServiceClient.requestSignOut({});
    }
}
